import type { Building } from "building-types/building/buildingTypes";
import { foundationFaceCategories } from "building-types/materials/faceCategories";
import type { FaceCategory } from "building-types/materials/faceCategories";
import type { AABB2 } from "geom/aabb2Types";
import { type Face, facesAABB3 } from "geom/brep";
import { attributedReportingUnitLabelId, collectReportingUnitRegions } from "lib/house/plans/reportingUnitRooms";

const FOUNDATION_FACE_CATEGORIES: ReadonlySet<FaceCategory> = new Set(foundationFaceCategories);

/** True for foundation face categories. */
export function isFoundationFaceCategory(faceCategory: FaceCategory): boolean {
  return FOUNDATION_FACE_CATEGORIES.has(faceCategory);
}

/** World-space XY footprint of a face group; null when the group is empty. */
export function faceGroupFootprintBB(faces: Face[]): AABB2 | null {
  if (faces.length === 0) {
    return null;
  }

  const { minX, minY, maxX, maxY } = facesAABB3(faces);
  return { minX, minY, maxX, maxY };
}

/**
 * Looks up the reporting unit a foundation face group sits under, null when it sits under none.
 * A callback so each unit's room regions are gathered once per building, not once per face group.
 */
export type FoundationFaceReportingUnitResolver = (faces: Face[]) => string | null;

/**
 * Builds a resolver attributing a foundation face group to the unit standing on it: generation gives
 * a whole level's foundation one handle, which can name only one unit. Null when no unit owns rooms.
 */
export function makeFoundationFaceReportingUnitResolver(
  building: Building,
): FoundationFaceReportingUnitResolver | null {
  const unitRegions = collectReportingUnitRegions(building);
  if (unitRegions.length === 0) {
    return null;
  }

  return (faces: Face[]): string | null => {
    const footprint = faceGroupFootprintBB(faces);
    return footprint ? attributedReportingUnitLabelId(unitRegions, footprint) : null;
  };
}

/** Memo; boxed because a building with no unit rooms caches a null resolver. */
const resolverByBuilding = new WeakMap<Building, { readonly resolver: FoundationFaceReportingUnitResolver | null }>();

/**
 * Cached; the resolver depends on the building alone, and one plan-set pass reaches it from several
 * independent paths that would otherwise each re-walk every unit's rooms.
 */
export function getFoundationFaceReportingUnitResolver(building: Building): FoundationFaceReportingUnitResolver | null {
  const cached = resolverByBuilding.get(building);
  if (cached) {
    return cached.resolver;
  }

  const resolver = makeFoundationFaceReportingUnitResolver(building);
  resolverByBuilding.set(building, { resolver });
  return resolver;
}

/**
 * Unit for one emitted geometry: the generator's own attribution first, then footprint attribution
 * for foundation. Null leaves the face to hosting-hierarchy resolution.
 */
export function resolveFoundationFaceReportingUnitLabelId(
  resolver: FoundationFaceReportingUnitResolver | null,
  faceCategory: FaceCategory,
  faces: Face[],
  reportingUnitLabelId?: string,
): string | null {
  if (reportingUnitLabelId !== undefined) {
    return reportingUnitLabelId;
  }
  if (!resolver || !isFoundationFaceCategory(faceCategory)) {
    return null;
  }
  return resolver(faces);
}
